import { z } from 'zod';
/**
 * "Connect this built-in Formant MCP with my own login" input.
 *
 * Used by the two first-party user-token built-ins — Formant Robotics
 * (`category=formant_telemetry`) and Formant Metaphysics
 * (`category=formant_metaphysics`) — in place of an OAuth redirect. The
 * console forwards the logged-in user's own tokens: the `accessToken` becomes
 * the org connection's bearer, and the optional `refreshToken` lets the API
 * re-mint the access token as it expires (opaque/refresh-less logins simply
 * stop working once the access token lapses and must reconnect).
 *
 * The token *source* (Keycloak vs. Formant Coherence/telemetry) is NOT taken
 * from the body — it is read from the caller's already-verified request
 * context (`principal.tokenSource`), which decides which endpoint refresh-on-
 * read calls. The connection is org-scoped: fleet agents, workflows and the
 * console sidebar run the MCP as these credentials, so the console gates the
 * action behind an org-wide-credential confirmation. Chat threads on the
 * shared Formant assistant are the exception — each turn's Formant tool calls
 * run as the person who sent the message (see `ChatActorCredentialSchema`).
 */
export const ConnectMcpUserTokenInputSchema = z.object({
    /** The caller's current access token — stored as the connection bearer. */
    accessToken: z.string().min(1),
    /** The caller's current refresh token, when the login exposes one. */
    refreshToken: z.string().min(1).optional(),
});
/** Which login minted the stored credentials (drives refresh-on-read). */
export const McpUserTokenSourceSchema = z.enum(['keycloak', 'robotics']);
/**
 * Connection status for a user-token built-in, safe to render in the console
 * (never carries secret material).
 */
export const McpUserTokenStatusSchema = z.object({
    /** True when a usable bearer is stored AND no definitive auth error is flagged. */
    connected: z.boolean(),
    /** The category slug of the built-in (`formant_telemetry` / `formant_metaphysics`). */
    category: z.string(),
    /** MCP endpoint URL of the org's connection row. */
    endpoint: z.string().nullable(),
    /** Which login backs the stored credentials, when connected. */
    tokenSource: McpUserTokenSourceSchema.nullable(),
    /** ISO timestamp the current access token expires at, when decodable. */
    tokenExpiresAt: z.string().nullable(),
    /** The org-owned MCPConnection row id backing this connection, when connected. */
    mcpConnectionId: z.string().nullable(),
    /**
     * Set when the last token refresh failed with a definitive auth error — an
     * HTTP 401, or a 400 carrying `error=invalid_grant`, from the refresh
     * endpoint: the stored refresh chain is dead and the connection must be
     * re-connected. Everything else, **403 included**, is transient (a WAF, a
     * rate limiter, an istio authz policy or a disabled org all answer 403
     * without the token being at fault) and leaves this unset. Cleared by a
     * successful refresh or reconnect.
     */
    authError: z.string().nullable(),
});
//# sourceMappingURL=formant-user-token.js.map